import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TaskPriority } from '@business/constants/task';
import { UserId, UserResponse } from '@business/entities/account';
import { TaskId, TaskResponse } from '@business/entities/task';
import { TaskStatusResponse } from '@business/entities/task_status';
import { CommonService, UserService } from '@business/services';
import { FormCheckbox, FormDropdownDate, FormInput } from '@core/components/form';
import { FormUserAvatar } from '@core/components/form/FormUserAvatar';
import { useHistory } from '@core/router';
import { formControl, formGroup } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { toMoment } from '@core/utils/time';
import { Session } from '@modules/auth/session';
import { PrioritySelector } from '@modules/common/PrioritySelector';
import { TableActions } from '@modules/common/TableActions';
import { TaskStatusSelector } from '@modules/common/TaskStatusSelector';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import { Button } from 'reactstrap';

function defineTaskForm() {
  return formGroup({
    checked: formControl<boolean>({}),
    due: formControl<number>({}),
    title: formControl<string>({}),
    assignee: formControl<UserId[]>({}),
  });
}
export interface TaskItemOldProps {
  task: TaskResponse;
  users: UserResponse[];
  onRemove(id: TaskId): void;
  onUpdate(id: TaskId, task: Partial<TaskResponse>): void;
  onComplete(id: TaskId, completed: boolean): void;
  isCompleted: boolean;
  parentSectionId: string;
  draggable?: boolean;
  taskStatuses: TaskStatusResponse[];
  listeners?: any;
}

export const TaskItemOld = (props: TaskItemOldProps) => {
  const { task, onUpdate, onRemove, onComplete, isCompleted, parentSectionId, draggable = true, taskStatuses } = props;
  const mNow = toMoment().unix();
  const [isHover, setHover] = useState(false);
  const form = useMemo(() => defineTaskForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const [isEdit, setEdit] = useState(false);
  const history = useHistory();
  const user = useObservable(Session.user$);

  useEffect(() => {
    form.patchValue({ checked: task.isCompleted, due: task.dueDate, assignee: task.assignee, title: task.title });
  }, [form, task]);

  const DragHandle = SortableHandle(() => <i className="bx bx-grid-vertical font-size-18 pl-2" style={{ cursor: 'pointer' }} />);

  const onPriority = useCallback(
    (priority: TaskPriority) => {
      onUpdate(task.id, { priority });
    },
    [onUpdate, task.id],
  );

  const onClick = useCallback(
    (taskId: TaskId) => {
      history.push(`/todos/${isCompleted ? 'completed' : 'pending'}/task/${taskId}`);
      // openTaskOverlay({ taskId });
    },
    [history, isCompleted],
  );

  return (
    <>
      <div
        className="task-item noselect task-tr-div"
        onMouseMove={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ height: task.organization ? 60 : 50 }}
      >
        <div className="task-td-div task-w-move">{isHover && draggable && !isCompleted && <DragHandle />}</div>

        <div className="task-td-div task-w-name">
          <div className="d-flex align-items-center" style={{ width: '100%' }}>
            <FormCheckbox noPadding control={form.controls.checked} onChange={value => onComplete(task.id, value)} />
            {isEdit && (
              <div className="d-flex flex-row align-items-center">
                <FormInput
                  autoFocus
                  control={form.controls.title}
                  withoutGroup
                  onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                    if (event.keyCode === 13) {
                      onUpdate(task.id, { title: formValue.title });
                      setEdit(false);
                    }
                  }}
                />

                <Button
                  size="sm"
                  outline
                  className="ml-2"
                  onClick={() => {
                    onUpdate(task.id, { title: formValue.title });
                    setEdit(false);
                  }}
                >
                  Save
                </Button>
                <a onClick={() => setEdit(false)}>
                  <i className="ml-2 fa fa-times p-2"></i>
                </a>
              </div>
            )}
            {!isEdit && (
              <div className="text-truncate">
                <a className="d-flex flex-column" onClick={() => onClick(task.id)}>
                  <span className="text-dark text-truncate">{task.title}</span>
                  <span style={{ fontSize: 10 }} className="text-muted">
                    {task.organization}
                  </span>
                </a>
                {task.isCompleted && task.completedBy && (
                  <div className="text-muted font-size-10">
                    <b>{CommonService.getFullName(task.completedBy)}</b> {toMoment(task.completedAt!, { utc: true }).fromNow()}
                  </div>
                )}
              </div>
            )}
            {isHover && !isEdit && <TableActions style={{ marginLeft: 10 }} onEdit={() => setEdit(true)} onDelete={() => onRemove(task.id)} />}
          </div>
        </div>
        <div className="task-td-div task-w-assignee">
          <FormUserAvatar
            tooltipContainer={parentSectionId}
            control={form.controls.assignee}
            users={props.users}
            meId={user?.id}
            onChange={assignee => assignee && onUpdate(task.id, { assignee })}
            addMe={false}
          />
        </div>
        {(!!taskStatuses.length || UserService.isAdmin(Session.user)) && (
          <div className="task-td-div task-w-assignee">
            <TaskStatusSelector
              statuses={taskStatuses}
              item={task.status}
              onSelect={status => onUpdate(task.id, { statusId: status ? status.id : '0' })}
            />
          </div>
        )}
        <div className="task-td-div task-w-due">
          <FormDropdownDate clearable control={form.controls.due} onChange={dueDate => onUpdate(task.id, { dueDate })}>
            <div className={`d-flex ml-2 ${toMoment(task.dueDate).unix() < mNow ? 'text-danger' : ''}`}>
              {!!task.dueDate && Formatter.formatTime(task.dueDate, 'M/D')}
              {!task.dueDate && <i className="bx bx-calendar font-size-16 text-primary"></i>}
            </div>
          </FormDropdownDate>
        </div>
        <div className="task-td-div task-w-priority">
          <PrioritySelector priority={task.priority} onSelect={priority => onPriority(priority)} />
        </div>
      </div>
    </>
  );
};

export const SortableTaskItem = SortableElement(TaskItemOld);
